import {
  CustomFieldResourceType,
  renderCustomFieldValueAsPlainText,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import type {
  TeammateAccountGrading,
  TranscriptMatchesByGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { useMemo, type FC, type ReactNode } from "react";
import { CustomFieldValue } from "../interviews/interviewSlideover/customFieldValue.js";
import {
  GradingCustomerExperienceScorePill,
  type CustomerExperienceScoreValue,
} from "./gradingCustomerExperienceScorePill.js";
import { GradingPassRate } from "./gradingPassRate.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";
import { GradingSectionSummariesContainer } from "./gradingSectionSummariesContainer.js";
import { GradingsTableContactCell } from "./gradingsTableContactCell.js";
import { GradingsTableTeammateCell } from "./gradingsTableTeammateCell.js";
import { IncorrectStatementsIndication } from "./incorrectStatementsIndication.js";
import { OutcomeValuePill } from "./outcomeValuePill.js";

export interface GradingsTableProps {
  isTeammateVisible?: boolean;
  /** Configures the Outcome column after the Playbook column. */
  outcomeDefinition?: OutcomeDefinition;
  gradings: readonly TeammateAccountGrading[];
  isLoading?: boolean;
  isError?: boolean;
  onGradingAction: (grading: TeammateAccountGrading) => void;
  onLoadMore?: () => void;
  selectedCustomFieldDefinitions?: readonly CustomFieldDefinition[];
  /** Whether to show the playbook adherence score column (controlled by section column selector) */
  showsPlaybookColumn?: boolean;
  /** Playbook sections selected for display as individual score columns */
  selectedSections?: readonly PlaybookSectionNode[];
  showsCustomerExperienceColumn?: boolean;
  /** Selected optional metrics; undefined shows every eligible metric. */
  selectedMetricColumnIds?: ReadonlySet<string>;
  isFullBleed?: boolean;
  /** Matched transcript snippet per grading id (present only during search). */
  snippetsByGradingId?: TranscriptMatchesByGrading;
  /**
   * Columns only one surface needs, placed after the shared ones (Customer,
   * Teammate, Playbook, section scores, Outcome, CX score) and before the
   * custom-field columns. Memoize at the call site — the table's own column
   * memo depends on the identity.
   */
  additionalColumns?: readonly EntityTableColumnDefinition<TeammateAccountGrading>[];
  /** Shown in place of rows when there are none; defaults to "No activity yet." */
  emptyMessage?: ReactNode;
}

const createColumns = (props: {
  isTeammateVisible?: boolean;
  outcomeDefinition?: OutcomeDefinition;
  selectedCustomFieldDefinitions?: readonly CustomFieldDefinition[];
  showsPlaybookColumn: boolean;
  selectedSections: readonly PlaybookSectionNode[];
  showsCustomerExperienceColumn: boolean;
  selectedMetricColumnIds?: ReadonlySet<string>;
  snippetsByGradingId?: TranscriptMatchesByGrading;
  additionalColumns: readonly EntityTableColumnDefinition<TeammateAccountGrading>[];
}): readonly EntityTableColumnDefinition<TeammateAccountGrading>[] => {
  const baseColumns: readonly EntityTableColumnDefinition<TeammateAccountGrading>[] =
    [
      {
        id: "customer",
        className: "w-[40%]",
        pinnedToLeft: true,
        header: "Customer",
        isRowHeader: true,
        renderCell: (grading) => (
          <GradingsTableContactCell
            grading={grading}
            snippet={props.snippetsByGradingId?.[grading.id]?.[0]}
          />
        ),
      },
      ...(props.isTeammateVisible
        ? [
            {
              id: "teammate",
              className: "w-[40%]",
              pinnedToLeft: true,
              header: "Teammate",
              renderCell: (grading: TeammateAccountGrading) => (
                <GradingsTableTeammateCell grading={grading} />
              ),
            },
          ]
        : []),
      ...(props.showsPlaybookColumn
        ? [
            {
              id: "playbookScore" as const,
              className: "max-w-[200px]",
              header: "Playbook",
              renderCell: (grading: TeammateAccountGrading) => {
                if (!grading.assessment) {
                  return null;
                }

                const passRate = grading.playbook_adherence_score ?? undefined;

                return (
                  <div className="flex items-start gap-3">
                    <div className="flex min-w-[50px] items-center gap-2">
                      <GradingPassRate size={Size.SMALL} passRate={passRate} />
                    </div>

                    <GradingSectionSummariesContainer
                      assessment={grading.assessment}
                    />

                    <IncorrectStatementsIndication
                      knowledgeBaseAdherence={grading.knowledge_base_adherence}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
    ];

  const otherColumns = [
    ...(props.outcomeDefinition &&
    (props.selectedMetricColumnIds?.has("outcome") ?? true)
      ? [
          {
            id: "outcome",
            className: "w-0",
            header: "Outcome",
            renderCell: (grading: TeammateAccountGrading) => (
              <OutcomeValuePill
                size={Size.SMALL}
                outcome={grading.outcome}
                outcomeDefinition={props.outcomeDefinition!}
              />
            ),
          },
        ]
      : []),
    ...(props.showsCustomerExperienceColumn &&
    (props.selectedMetricColumnIds?.has("cxScore") ?? true)
      ? [
          {
            id: "cxScore" as const,
            className: "w-0",
            header: (
              <TooltipTrigger>
                <TooltipHoverTarget>CX score</TooltipHoverTarget>
                <SimpleTooltip>
                  How the customer felt by the end of the interaction, scored
                  1&ndash;5.
                </SimpleTooltip>
              </TooltipTrigger>
            ),
            renderCell: (grading: TeammateAccountGrading) => (
              <GradingCustomerExperienceScorePill
                score={
                  (grading.customer_experience_score ??
                    null) as CustomerExperienceScoreValue | null
                }
              />
            ),
          },
        ]
      : []),
  ];

  // Add playbook section score columns
  const sectionColumns: EntityTableColumnDefinition<TeammateAccountGrading>[] =
    (props.selectedSections ?? []).map((section) => ({
      id: `section-${section.id}`,
      className: "w-0",
      header: (
        <TooltipTrigger>
          <TooltipHoverTarget className="block max-w-[100px] truncate">
            {section.name}
          </TooltipHoverTarget>
          <SimpleTooltip>&ldquo;{section.name}&rdquo; adherence</SimpleTooltip>
        </TooltipTrigger>
      ),
      renderCell: (grading) => {
        const sectionGrade = grading.assessment?.section_grading?.[section.id];

        // Missing
        if (!sectionGrade) {
          return <span className="text-muted-foreground">--</span>;
        }

        const { is_not_applicable, criteria_pass_count, criteria_total_count } =
          sectionGrade;

        // Not applicable
        if (
          is_not_applicable ||
          criteria_pass_count === undefined ||
          criteria_total_count === undefined ||
          criteria_total_count === 0
        ) {
          return <GradingPassRatePill size={Size.SMALL} passRate={undefined} />;
        }

        const passRate = criteria_pass_count / criteria_total_count;
        return <GradingPassRatePill size={Size.SMALL} passRate={passRate} />;
      },
    }));

  // Add custom field columns
  const customFieldColumns: EntityTableColumnDefinition<TeammateAccountGrading>[] =
    (props.selectedCustomFieldDefinitions ?? []).map((fieldDef) => ({
      id: `custom-field-${fieldDef.id}`,
      className: "w-0 max-w-[250px]",
      header: fieldDef.display_name,
      renderCell: (grading) => {
        // For grading-level custom fields (data extraction), use grading_custom_fields
        if (fieldDef.resource_type === CustomFieldResourceType.GRADING) {
          const customField = grading.grading_custom_fields.find(
            (field) => field.custom_field_definition_id === fieldDef.id,
          );

          if (!customField) {
            return <span className="text-gray-400">--</span>;
          }

          return (
            <div className="line-clamp-3">
              <CustomFieldValue field={customField} disregardDisplayValue />
            </div>
          );
        }

        // For contact/account custom fields, use the value from the first contact
        // TODO: Handle multiple contacts - currently showing first contact's value only
        const firstContact = grading.contacts[0];
        if (!firstContact) {
          return <span className="text-gray-400">--</span>;
        }

        const customField = firstContact.custom_fields.find(
          (field) => field.custom_field_definition_id === fieldDef.id,
        );

        if (!customField) {
          return <span className="text-gray-400">--</span>;
        }

        return (
          <span className="line-clamp-3 text-sm text-foreground">
            {renderCustomFieldValueAsPlainText(customField)}
          </span>
        );
      },
    }));

  return [
    ...baseColumns,
    ...sectionColumns,
    ...otherColumns,
    ...props.additionalColumns,
    ...customFieldColumns,
  ];
};

const NO_ADDITIONAL_COLUMNS: readonly EntityTableColumnDefinition<TeammateAccountGrading>[] =
  [];

export const GradingsTable: FC<GradingsTableProps> = (props) => {
  const columns = useMemo(
    () =>
      createColumns({
        isTeammateVisible: props.isTeammateVisible,
        selectedMetricColumnIds: props.selectedMetricColumnIds,
        outcomeDefinition: props.outcomeDefinition,
        selectedCustomFieldDefinitions: props.selectedCustomFieldDefinitions,
        showsPlaybookColumn: props.showsPlaybookColumn ?? true,
        selectedSections: props.selectedSections ?? [],
        showsCustomerExperienceColumn:
          props.showsCustomerExperienceColumn ?? false,
        snippetsByGradingId: props.snippetsByGradingId,
        additionalColumns: props.additionalColumns ?? NO_ADDITIONAL_COLUMNS,
      }),
    [
      props.isTeammateVisible,
      props.selectedMetricColumnIds,
      props.outcomeDefinition,
      props.selectedCustomFieldDefinitions,
      props.showsPlaybookColumn,
      props.selectedSections,
      props.showsCustomerExperienceColumn,
      props.snippetsByGradingId,
      props.additionalColumns,
    ],
  );

  // While (re)loading new results, drop stale results and show skeleton instead
  const entities = useMemo(
    () => (props.isLoading ? [] : props.gradings),
    [props.isLoading, props.gradings],
  );

  return (
    <EntityTable
      aria-label="Recordings"
      columns={columns}
      entities={entities}
      isLoading={props.isLoading}
      isError={props.isError}
      onLoadMore={props.onLoadMore}
      emptyMessage={props.emptyMessage ?? "No activity yet."}
      onEntityAction={props.onGradingAction}
      size={Size.SMALL}
      isFullBleed={props.isFullBleed}
    />
  );
};
